// client/src/app/LayoutClient.tsx
"use client";

import React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { PortfolioProviderWithQuery } from '../contexts/PortfolioContext';
import { DashboardProvider } from '../contexts/DashboardContext';
import { CookieConsentProvider } from '../contexts/CookieConsentContext';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import PublicNavbar from '@/components/PublicNavbar';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import ScrollToTop from '@/components/ScrollToTop';
import ErrorBoundary from '@/components/ErrorBoundary';
import TrialBanner from '@/components/TrialBanner';
import { trackSessionEnded } from '@/analytics/posthog';
import { installChunkReloadGuard } from '@/utils/chunkReload';
import { pageOwnsAuthRedirect } from './serverAuthProtected';
import { loginUrlFor } from '@/utils/safeReturnPath';

// DEL-1012: keep in sync with AuthContext.tsx AUTH_ME_SKIPPABLE_PUBLIC_PREFIXES
// (authMeSkipPublicRoutes.test.ts asserts these two lists are equal). /pricing
// has no /pricing/* subroutes today, so adding it to the prefix list is a
// no-op for routing -- it's only here to satisfy the sync guard.
//
// DEL-1812: /login was previously in PUBLIC_ROUTES (exact match) but not in
// PUBLIC_PREFIXES. Now that AuthContext skips /api/auth/me on /login to make
// the post-signin /me refetch the only call in the sign-in flow, /login must
// also live here so the cross-file sync assertion stays green. The prefix
// matcher `pathname.startsWith(prefix)` still scopes it to /login and any
// /login/* subroutes; nothing else shares that namespace.
// 2026-10-18: the public calculator/tool/content routes below mirror the
// AuthContext skip list (same sync guard). As prefixes they also cover their
// subpages; none of them is the start of a protected route.
const PUBLIC_PREFIXES = ['/features', '/guides', '/help', '/comparison', '/api', '/blog', '/reassurance', '/stats', '/pricing', '/login', '/uk-fire-calculator', '/tools', '/calculators', '/security', '/terms', '/privacy', '/cookies', '/why-delphina-exists', '/glossary', '/income-tax-calculator', '/compound-interest-calculator', '/compound-interest', '/fire-calculator', '/isa-calculator', '/mortgage-calculator', '/pension-calculator', '/pension-contribution-calculator', '/savings-calculator', '/savings-goal', '/retirement-planner', '/stamp-duty', '/student-loan-repayment', '/cost-of-living', '/safe-withdrawal-rate', '/savings-rates', '/will-i-run-out-of-money', '/take-home-pay', '/mortgage-payoff', '/monte-carlo-retirement-calculator', '/affiliates', '/ai-information', '/am-i-on-track', '/are-you-on-track', '/book', '/brokers', '/can-i-retire-at', '/coaching', '/compound-growth', '/employee-benefits', '/financial-clarity-check', '/financial-coaching', '/financial-therapy', '/fire', '/how-much-pension-do-i-need', '/inheritance-planning-check', '/is-delphina-legit', '/isa-health-check', '/isa-vs-sipp', '/landlords', '/loans', '/locations', '/money-management', '/mortgage-or-invest', '/mortgage-vs-savings', '/pension-pot-by-age', '/pension-providers', '/pension-shortfall-calculator', '/personal-finance', '/press', '/pricing-comparison', '/research', '/retirement-readiness-check', '/reviews', '/state-pension-estimate', '/videos', '/where-can-i-retire-today', '/whitepaper'];
const PUBLIC_ROUTES = ['/', '/login', '/verify-email', '/pricing', '/security', '/terms', '/privacy', '/cookies', '/why-delphina-exists', '/glossary', '/comparison', '/tools', '/income-tax-calculator', '/compound-interest-calculator', '/compound-interest', '/fire-calculator', '/isa-calculator', '/mortgage-calculator', '/pension-calculator', '/pension-contribution-calculator', '/savings-calculator', '/savings-goal', '/retirement-planner', '/calculators/when-can-i-retire', '/stamp-duty', '/student-loan-repayment', '/cost-of-living', '/safe-withdrawal-rate', '/savings-rates', '/will-i-run-out-of-money', '/take-home-pay', '/mortgage-payoff', '/monte-carlo-retirement-calculator'];

function isPublicPath(pathname: string | null): boolean {
  return !!pathname && (PUBLIC_ROUTES.includes(pathname) || PUBLIC_PREFIXES.some(prefix => pathname.startsWith(prefix)));
}

function isLikely404Path(pathname: string | null): boolean {
  return !!pathname && pathname !== '/' &&
    !isPublicPath(pathname) &&
    !pathname.startsWith('/dashboard') &&
    !pathname.startsWith('/assets') &&
    !pathname.startsWith('/transactions') &&
    !pathname.startsWith('/income') &&
    !pathname.startsWith('/expenses') &&
    !pathname.startsWith('/settings') &&
    !pathname.startsWith('/subscription') &&
    !pathname.startsWith('/subscription-settings') &&
    !pathname.startsWith('/admin');
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, authChecked } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  // Redirect to home page if not authenticated and not on a public route
  React.useEffect(() => {
    // Add a small delay to allow OAuth flags to be set
    const timer = setTimeout(() => {
      // Check if we're in an OAuth flow by looking for OAuth flags
      const isOAuthFlow = typeof window !== 'undefined' && (
        localStorage.getItem('force_dashboard_refresh') ||
        localStorage.getItem('new_default_scenario_id') ||
        localStorage.getItem('new_portfolio_id')
      );

      console.log('[LayoutClient] Auth check:', { user: !!user, loading, authChecked, pathname, isOAuthFlow });

      // DEL-864: the dashboard redirect below is removed because the dashboard
      // page has its own redirect effect (DashboardClient.tsx, line ~325) that
      // consults the SSR-provided serverAuthState. When the upstream Express
      // auth proxy flaps (rate-limited under DEL-831, ECONNRESET during a
      // Nodemon restart) the client-side /api/auth/me can fail and the user
      // state stays null even though the auth_session cookie is still valid.
      // LayoutClient previously treated `!user && authChecked` on /dashboard as
      // "signed out" and pushed /, which bounced signed-in users off the
      // dashboard before DashboardClient could consult serverAuthState. Skip
      // the dashboard branch here; DashboardClient is authoritative for /dashboard.
      //
      // DEL-902: extend the same carve-out to the other protected routes whose
      // page owns the redirect (see serverAuthProtected.ts). The Mobile Chrome
      // chart-data-debug test was unmounting /income mid-form-fill because
      // this redirect fires ~500 ms after hydration on CPU-throttled mobile
      // emulation, before /api/auth/me has answered.
      const shouldRedirectToHome =
        !user && authChecked && !isOAuthFlow && !pageOwnsAuthRedirect(pathname);

      if (shouldRedirectToHome) {
        console.log('[LayoutClient] Redirecting to home - user not authenticated and not in OAuth flow');
        // Check if this is a protected route
        if (!isPublicPath(pathname) && !isLikely404Path(pathname)) {
          trackSessionEnded('no_session_redirect', { source: 'layout' });
          // Send them to sign in and back here afterwards, so a link from an
          // email (e.g. the Money Day "update your numbers" button) still lands.
          router.push(loginUrlFor(`${pathname}${window.location.search}`));
        }
      }
    }, 100); // 100ms delay to allow OAuth flags to be set

    return () => clearTimeout(timer);
  }, [user, loading, authChecked, pathname, router]);

  // Check if current route is public or likely 404
  const isPublic = isPublicPath(pathname);
  const isLikely404 = isLikely404Path(pathname);

  if (isPublic || isLikely404) {
    return <>{children}</>;
  }

  // DEL-864: the dashboard has its own redirect effect (DashboardClient.tsx,
  // ~line 325) that consults the SSR-provided serverAuthState. LayoutClient's
  // `!user => return null` block was bouncing signed-in users off /dashboard
  // whenever the client-side /api/auth/me call failed (Express auth rate-limit
  // hit, ECONNRESET, slow Turbopack compile) because the page-level render
  // block never ran to consult serverAuthState. Let DashboardClient render
  // unconditionally for /dashboard; it returns null itself when both
  // serverAuthState.isAuthenticated and user are false (DashboardClient.tsx
  // ~line 797).
  //
  // DEL-902: same carve-out for the other SERVER_AUTH_PROTECTED routes.
  // Their page client components own the unauthenticated-redirect (via
  // serverAuthState), so LayoutClient must not return null here, or those
  // pages get unmounted mid-render and never get a chance to handle their
  // own auth state.
  if (pageOwnsAuthRedirect(pathname)) {
    return <>{children}</>;
  }

  // Show loading while checking authentication, including after the safety
  // timer has cleared `loading` but the session check hasn't answered yet.
  // DEL-739: replaced <LoadingSpinner /> (position:fixed, caused CLS by giving
  // the page zero height during SSR hydration) with a reserved-height skeleton.
  if (loading || (!user && !authChecked)) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ minHeight: '100vh' }}>
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Don't render anything if not authenticated - redirect effect will handle it
  if (!user) {
    return null;
  }

  return <>{children}</>;
}


export default function LayoutClient({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const pathname = usePathname();
  const isAdminRoute = pathname?.startsWith('/admin');
  const isEmbedRoute = pathname?.endsWith('/embed');
  const isBareRoute = isAdminRoute || isEmbedRoute;
  const [paddingTop, setPaddingTop] = React.useState<string>('104px');

  React.useEffect(() => installChunkReloadGuard(), []);

  React.useEffect(() => {
    const computePaddingTop = () =>
      window.innerWidth < 500 ? '90px' : '104px';

    const handleResize = () => setPaddingTop(computePaddingTop());

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-primary">
        {!isBareRoute && (user ? <Navbar /> : <PublicNavbar />)}
        <ImpersonationBanner />
        {!isBareRoute && (
          <div
            role="note"
            aria-label="Legal disclaimer"
            className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-100 text-gray-800 border-t-2 border-gray-300 text-center leading-tight py-0.5 px-3 text-xs font-medium"
          >
            Educational use only. Not financial, investment, tax or legal advice.
          </div>
        )}
        <div className={user && !isBareRoute ? "pt-8 pb-8" : isBareRoute ? "pt-0 pb-0" : user ? "pt-24 pb-8" : "pt-90 sm:pt-104 pb-8"} style={{ paddingTop: isBareRoute ? '0px' : user ? '24px' : paddingTop }}>
          <ScrollToTop />
          {user && !isBareRoute && <TrialBanner />}
          <PrivateRoute>{children}</PrivateRoute>
        </div> </div>
    </ErrorBoundary >
  );
}

// Wrapper for providers so they are only initialized once
export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CookieConsentProvider>
          <PortfolioProviderWithQuery>
            <DashboardProvider>
              {children}
            </DashboardProvider>
          </PortfolioProviderWithQuery>
        </CookieConsentProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
